/**
 * Image model definitions for BrandReel.
 * Full capability reference: docs/IMAGE_MODELS.md
 */
export type ImageModel =
	| "nano-banana-2-lite"
	| "nano-banana-2"
	| "recraft-v4-1"
	| "recraft-v4-1-pro"
	| "seedream-5.0-pro"
	| "gpt-image-2";
export type LegacyImageModel = "gpt-image" | "recraft-v4" | "recraft-v4-pro";
export type AspectRatio = "1:1" | "4:3" | "3:4" | "16:9" | "9:16";

export const DEFAULT_IMAGE_MODEL: ImageModel = "nano-banana-2";

export type ImageSize = "1K" | "2K";

export interface ImageModelDef {
	id: ImageModel;
	label: string;
	description: string;
	/** Single uppercase letter shown in the icon badge */
	badge: string;
	/** Aspect ratios supported by this model */
	aspectRatios: AspectRatio[];
	/** Image sizes supported by this model. Defaults to ["1K", "2K"] if omitted. */
	imageSizes?: ImageSize[];
	/** Default image size for this model. Falls back to "1K" if omitted. */
	defaultImageSize?: ImageSize;
	/** Whether this model supports reference images. Defaults to true. */
	supportsReferenceImages?: boolean;
	/** Maximum reference images accepted by the provider. Defaults to 3. */
	maxReferenceImages?: number;
}

export const IMAGE_MODELS: ImageModelDef[] = [
	{
		id: "nano-banana-2-lite",
		label: "Nano Banana 2 Lite",
		description: "Fast, cost-efficient Google generation for previews",
		badge: "G",
		aspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
		imageSizes: ["1K"],
	},
	{
		id: "nano-banana-2",
		label: "Nano Banana 2",
		description: "Google's flagship generation model",
		badge: "G",
		aspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
	},
	{
		id: "recraft-v4-1",
		label: "Recraft V4.1",
		description: "Fast professional design and marketing generation",
		badge: "R",
		aspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
		imageSizes: ["1K"],
		supportsReferenceImages: false,
	},
	{
		id: "recraft-v4-1-pro",
		label: "Recraft V4.1 Pro",
		description: "Premium 2K generation for brand-critical creative production",
		badge: "R",
		aspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
		imageSizes: ["2K"],
		supportsReferenceImages: false,
	},
	{
		id: "seedream-5.0-pro",
		label: "Seedream 5.0 Pro",
		description:
			"ByteDance flagship generation and editing with strong text rendering",
		badge: "S",
		aspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
		imageSizes: ["1K", "2K"],
		defaultImageSize: "1K",
		maxReferenceImages: 10,
	},
	{
		id: "gpt-image-2",
		label: "GPT Image 2",
		description: "OpenAI's latest image model via Runware",
		badge: "O",
		aspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
		defaultImageSize: "2K",
	},
];

export const LEGACY_IMAGE_MODEL_LABELS: Record<LegacyImageModel, string> = {
	"gpt-image": "GPT Image 1.5",
	"recraft-v4": "Recraft V4",
	"recraft-v4-pro": "Recraft V4 Pro",
};

const IMAGE_MODEL_ID_SET = new Set<ImageModel>(IMAGE_MODELS.map((m) => m.id));

export function isImageModel(value: unknown): value is ImageModel {
	return (
		typeof value === "string" && IMAGE_MODEL_ID_SET.has(value as ImageModel)
	);
}

export function getImageModelDef(model: ImageModel): ImageModelDef | undefined {
	return IMAGE_MODELS.find((item) => item.id === model);
}

export function getImageModelDisplayLabel(
	modelId: string | null | undefined,
): string | null {
	if (!modelId) return null;
	const activeModel = IMAGE_MODELS.find((model) => model.id === modelId);
	if (activeModel) return activeModel.label;
	if (modelId in LEGACY_IMAGE_MODEL_LABELS) {
		return LEGACY_IMAGE_MODEL_LABELS[modelId as LegacyImageModel];
	}
	return null;
}
